import { LanguageVariantsSection } from "@/components/language-variants-section";
import { BuilderShell } from "@/components/studio/builder-shell";
import { IntentTaggingSection } from "@/components/studio/intent-tagging-section";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { categoryService } from "@/lib/api/categories.service";
import { coachService } from "@/lib/api/coachService";
import { ContentTagService } from "@/lib/api/content.tag.service";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";
import type { Category, Coach, TagGroup, TagOption } from "@/lib/schema";
import {
  CONTENT_TYPE_CONFIG,
  SUPPORTED_LANGUAGES,
} from "@/lib/studio-constants";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { ChallengeVariantFields } from "./challenge-variant-fields";

const challengeConfig = CONTENT_TYPE_CONFIG.challenge;
const ELEVATED_ROLES = [
  "admin",
  "super_admin",
  "coach_manager",
  "content_manager",
];

interface LanguageVariantDraft {
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  audioContentId: string | null;
  audioFileUrl: string | null;
  audioFileName: string | null;
  durationSeconds: number | null;
  thumbnailUrl: string | null;
}

export default function ChallengeCreatePage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const isCoachOnly =
    !!user?.roles?.includes("coach") &&
    !user.roles.some((r) => ELEVATED_ROLES.includes(r));
  const selfCoachId = user?.id ?? null;

  const [categoryId, setCategoryId] = useState("");
  const [authorCoachId, setAuthorCoachId] = useState<string | null>(null);
  const [selectedTagOptionIds, setSelectedTagOptionIds] = useState<string[]>(
    [],
  );
  const [variants, setVariants] = useState<LanguageVariantDraft[]>([
    {
      languageCode: "en",
      isDefault: true,
      localizedTitle: "",
      audioContentId: null,
      audioFileUrl: null,
      audioFileName: null,
      durationSeconds: null,
      thumbnailUrl: null,
    },
  ]);
  const [activeVariantIndex, setActiveVariantIndex] = useState(0);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/content-category"],
    queryFn: () => categoryService.findAll(),
  });

  const { data: tagGroups = [] } = useQuery<TagGroup[]>({
    queryKey: ["/content/tag-groups"],
    queryFn: () => ContentTagService.getTagGroups(),
  });

  const { data: tagOptions = [] } = useQuery<TagOption[]>({
    queryKey: ["/content/tag-options"],
    queryFn: () => ContentTagService.getTagOptions(),
  });

  const { data: coaches = [] } = useQuery<Coach[]>({
    queryKey: ["/api/coaches"],
    queryFn: () => coachService.getCoaches().then((r) => r.coaches),
    enabled: !isCoachOnly,
  });

  useEffect(() => {
    if (isCoachOnly && selfCoachId && !authorCoachId) {
      setAuthorCoachId(selfCoachId);
    }
  }, [isCoachOnly, selfCoachId, authorCoachId]);

  const defaultVariant = variants.find((v) => v.isDefault) ?? variants[0];
  const canSubmit = !!defaultVariant?.localizedTitle.trim();

  const createMutation = useMutation({
    mutationFn: async (submitForApproval: boolean) => {
      if (!canSubmit) throw new Error("Default variant needs a title");

      const languages = variants
        .filter((v) => v.localizedTitle.trim())
        .map((v) => ({
          languageCode: v.languageCode,
          isDefault: v.isDefault,
          localizedTitle: v.localizedTitle,
          audioContentId: v.audioContentId || undefined,
        }));

      const challenge = await api.post<{ id: string }>("/content-challenges", {
        categoryId: categoryId || undefined,
        authorCoachId: authorCoachId || undefined,
        tagOptionIds: selectedTagOptionIds,
        version: { languages },
      });

      if (submitForApproval) {
        await api.post(`/content-challenges/${challenge.id}/submit`, {});
      }

      return challenge;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/content-challenges"] });
      toast({ title: "Challenge created" });
      setLocation(`/studio/challenges/${data.id}/edit`);
    },
    onError: (error: Error) => {
      toast({
        title: error.message || "Failed to create challenge",
        variant: "destructive",
      });
    },
  });

  const updateVariant = (field: keyof LanguageVariantDraft, value: any) => {
    setVariants((prev) => {
      const updated = [...prev];
      updated[activeVariantIndex] = {
        ...updated[activeVariantIndex],
        [field]: value,
      };
      return updated;
    });
  };

  const addLanguageVariant = (langCode: string) => {
    setVariants((prev) => {
      if (prev.some((v) => v.languageCode === langCode)) return prev;
      const next = [
        ...prev,
        {
          languageCode: langCode,
          isDefault: false,
          localizedTitle: "",
          audioContentId: null,
          audioFileUrl: null,
          audioFileName: null,
          durationSeconds: null,
          thumbnailUrl: null,
        },
      ];
      setActiveVariantIndex(next.length - 1);
      return next;
    });
  };

  const confirmRemoveLanguageVariant = () => {
    if (pendingRemoveIndex === null) return;
    if (variants[pendingRemoveIndex].isDefault) return;
    if (variants.length <= 1) {
      toast({
        title: "At least one language variant must remain",
        variant: "destructive",
      });
      setPendingRemoveIndex(null);
      return;
    }
    const updated = variants.filter((_, i) => i !== pendingRemoveIndex);
    setVariants(updated);
    if (activeVariantIndex >= updated.length) {
      setActiveVariantIndex(Math.max(0, updated.length - 1));
    }
    setPendingRemoveIndex(null);
  };

  const setAsDefault = (index: number) => {
    if (index < 0 || index >= variants.length) return;
    const updated = variants.map((v, i) => ({ ...v, isDefault: i === index }));
    setVariants(updated);
    setActiveVariantIndex(index);
  };

  const toggleTagOption = (id: string) => {
    setSelectedTagOptionIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
    );
  };

  const getGroupOptions = (groupId: string) =>
    tagOptions.filter((to) => to.groupId === groupId);

  const availableLanguages = SUPPORTED_LANGUAGES.filter(
    (lang) => !variants.some((v) => v.languageCode === lang.code),
  );

  const activeVariant = variants[activeVariantIndex];

  return (
    <BuilderShell
      contentTypeConfig={challengeConfig}
      mode="create"
      onSave={() => createMutation.mutate(false)}
      onSubmit={() => createMutation.mutate(true)}
      isSaving={createMutation.isPending}
      isSubmitting={createMutation.isPending}
      status="draft"
    >
      <IntentTaggingSection
        stepNumber={1}
        categories={categories}
        categoryId={categoryId}
        onCategoryChange={setCategoryId}
        tagGroups={tagGroups}
        getGroupOptions={getGroupOptions}
        selectedTagOptionIds={selectedTagOptionIds}
        onToggleTagOption={toggleTagOption}
      />

      <Card className="p-4 space-y-2">
        <Label>Author Coach</Label>
        {isCoachOnly ? (
          <Input
            value={user?.name ?? ""}
            disabled
            data-testid="select-author-coach"
          />
        ) : (
          <Select
            value={authorCoachId || "none"}
            onValueChange={(val) =>
              setAuthorCoachId(val === "none" ? null : val)
            }
          >
            <SelectTrigger data-testid="select-author-coach">
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {coaches.map((coach) => (
                <SelectItem key={coach.id} value={coach.id}>
                  {coach.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Card>

      <LanguageVariantsSection
        stepNumber={2}
        variants={variants}
        activeVariantIndex={activeVariantIndex}
        onActiveVariantChange={setActiveVariantIndex}
        availableLanguages={availableLanguages}
        onAddVariant={addLanguageVariant}
        onSetDefault={setAsDefault}
        pendingRemoveIndex={pendingRemoveIndex}
        onRequestRemove={setPendingRemoveIndex}
        onConfirmRemove={confirmRemoveLanguageVariant}
        onCancelRemove={() => setPendingRemoveIndex(null)}
      >
        {activeVariant && (
          <ChallengeVariantFields
            variant={activeVariant}
            onFieldChange={updateVariant}
            currentCategoryId={categoryId || null}
            currentTagOptionIds={selectedTagOptionIds}
            allTagOptions={tagOptions}
          />
        )}
      </LanguageVariantsSection>
    </BuilderShell>
  );
}
